import { useState } from "react"
import { Button, Divider, Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { regimeConditionData } from "routes/rule-sets/controller/regime-condition-data"
import { RuleSetNameSection } from "../rule-sets/rule-set-name-section"
import { DefaultActionSelector } from "../rule-sets/default-action-selector"
import { RuleListEditor } from "../rule-sets/rule-list-editor"
import { ErrorAlert } from "../rule-sets/error-alert"
import { WarningAlert } from "../rule-sets/warning-alert"
import { validateRuleSet } from "../rule-sets/validate-rule-set"
import { CONDITION_FIELDS } from "../rule-sets/condition-field-config"

export function CreateRuleSetInScenarioDialog({ ok, cancel, regime }) {
    const [name, setName] = useState("")
    const [description, setDescription] = useState("")
    const [defaultAction, setDefaultAction] = useState("include")
    const [rules, setRules] = useState([])

    const regimeData = regimeConditionData.useResults(regime?.id ? { regimeId: regime.id } : null)

    const validation = validateRuleSet({ name, description, defaultAction, rules })

    return (
        <Bound conditionFields={CONDITION_FIELDS} regimeData={regimeData}>
            <Stack data-cy="create-rule-set-in-scenario" spacing={3} sx={{ p: 3 }}>
                <Stack spacing={0.5}>
                    <Typography variant="h6">Create Rule Set</Typography>
                    <If value={regime?.label}>
                        <Typography variant="caption" color="text.secondary">
                            Rules use data from {regime?.label}
                        </Typography>
                    </If>
                </Stack>
                <RuleSetNameSection
                    name={name}
                    description={description}
                    onNameChange={setName}
                    onDescriptionChange={setDescription}
                />
                <Divider />
                <DefaultActionSelector value={defaultAction} onChange={setDefaultAction} />
                <If value={validation.errors.length > 0}>
                    <Repeat list={validation.errors} item={<ErrorAlert />} />
                </If>
                <If value={validation.warnings.length > 0}>
                    <Repeat list={validation.warnings} item={<WarningAlert />} />
                </If>
                <Divider />
                <RuleListEditor
                    rules={rules}
                    onAdd={handleAddRule}
                    onUpdate={handleUpdateRule}
                    onRemove={handleRemoveRule}
                    onReorder={handleReorder}
                    helperText="Rules are evaluated in order. The first matching rule determines the action for each task."
                />
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                    <Button onClick={cancel}>Cancel</Button>
                    <Button
                        data-cy="create-rule-set-in-scenario-submit"
                        variant="contained"
                        disabled={validation.errors.length > 0}
                        onClick={handleSubmit}
                    >
                        Create
                    </Button>
                </Stack>
            </Stack>
        </Bound>
    )

    function handleAddRule(newRule) {
        setRules((prev) => [...prev, newRule])
    }

    function handleUpdateRule(ruleId, updatedRule) {
        setRules((prev) => prev.map((r) => (r.id === ruleId ? { ...r, ...updatedRule } : r)))
    }

    function handleRemoveRule(ruleId) {
        setRules((prev) => prev.filter((r) => r.id !== ruleId))
    }

    function handleReorder(reordered) {
        setRules(reordered)
    }

    function handleSubmit() {
        ok({
            name: name.trim(),
            description,
            defaultAction,
            rules,
            regime: regime || null,
        })
    }
}
